import type {
  AvlusingSvar, LiceCount, LiceCurrent, LiceTreatment, Pil, RegisterWeek,
  SymbolTable, Symbolkode,
  Varsel as VarselData, VarselUke,
} from '../api'
import { useT } from '../lang'
import { Eq } from './Loading'

/* The lice forecast, laid out the way a weather forecast is: one row per week,
 * oldest at the top, reading downward into the future. Three weeks of what
 * happened, then the weeks the model will commit to.
 *
 * NOTHING IN A ROW BUT SYMBOLS AND NUMBERS. That is the whole contract, and it
 * is the one yr.no keeps. Every sentence that used to sit in these cells was a
 * caption restating its own picture — "middels, stiger mot grensen" beside two
 * lice and a rising arrow. The proof did not go away; it moved to the legend,
 * which is closed by default and is where a reader goes to check a claim
 * rather than to read one.
 *
 * **Quantity by repetition, never by scaling.** Two lice are two identical
 * glyphs, not one drawn twice as large, because the eye reads count correctly
 * and area badly. Direction is a separate sign beside them.
 *
 * **The set is closed** — eight forecasts, one louse, four arrows — so there
 * cannot be a symbol nobody has counted the frequency of.
 *
 * **The limit is read per week.** 0,50 normally, 0,20 in weeks 16–21, 0,10
 * there in a red production area, and five weeks later than that north of
 * Nord-Trøndelag. Taking the first week's limit and reusing it would be
 * invisible today, when every week is 0,50, and wrong in April.
 *
 * **ROWS ARE LABELLED BY DATE, NEVER BY HORIZON INDEX.** `h=1` is not "next
 * week". The forecast starts at its own origin, and that origin is the farm's
 * count only if one reached the model — otherwise it is the register, which
 * runs about two weeks behind. On a register-anchored site `h=1` has already
 * happened. Every label here is computed from the row's own Monday against
 * this Monday, which is the only way the word "neste uke" can be trusted.
 *
 * **An observation wears no arrow.** The louse count is a level, and a level
 * is a published rule any value can be read against — under half the limit,
 * half to whole, at or over. The ARROW is not: it is the model's own
 * classification, fitted and counted, and there is no honest way to put one on
 * a week that has already happened. So past weeks get lice and a number, the
 * future gets lice, an arrow and an interval, and the difference between a
 * measurement and a forecast is visible without a word being spent on it.
 *
 * **The warning means do something.** A weather warning is not a stronger
 * adjective, it is an instruction, and this one sits on the first week the
 * lice reach the limit — the week the delousing has to happen in.
 */

export const nb = (n: number, d = 2) =>
  n.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d })

/** Percent, as in a weather forecast. The floor and the ceiling are what make
 *  it honest: "0 %" is not none, it is a rounded small number, and none is the
 *  one claim a forecast must never make. Hard space, which is Norwegian
 *  typesetting. */
function prosent(x: number): string {
  if (x >= 0.995) return 'over 99 %'
  if (x >= 0.01) return `${Math.round(x * 100)} %`
  return 'under 1 %'
}

/** ISO week number. The Thursday rule, not day/7 — 2026 has 53 weeks. */
export function isoUke(iso: string): number {
  const d = new Date(iso)
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7))
  const nyttaar = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  return Math.ceil(((t.getTime() - nyttaar.getTime()) / 86400000 + 1) / 7)
}

/** The ISO YEAR, which is not the calendar year in the last days of December
 *  or the first of January — 2027-01-01 is ISO 2026-W53. A boat week sent with
 *  the wrong one of the two is a boat a year away. */
export function isoAar(iso: string): number {
  const d = new Date(iso)
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7))
  return t.getUTCFullYear()
}

export const dagMnd = (iso: string) =>
  new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })

/** The level band, derived from the number of lice. The same three classes the
 *  model uses: under half the limit, half to whole, over. */
const baand = (antall: number) =>
  antall <= 1 ? 'lavt' : antall === 2 ? 'middels' : 'over'

/* ------------------------------------------------------------- pictograms */

/** Adult female louse, filled silhouette: the shield, the abdomen, two egg
 *  strings. It is the shape you recognise from the counting tray.
 *
 *  Always the same size. Quantity is repetition — see the header.
 *
 *  Exported because `PerTiFisk` counts the same animal in the same shape. Two
 *  drawings of a salmon louse on one page would be two animals to a reader who
 *  has never seen either. */
export function Louse() {
  return (
    <svg viewBox="0 0 20 24" width="25" height="30" fill="currentColor"
      className="glyf-lus" aria-hidden="true">
      {/* Carapace: broad, shield-like, wider than tall. The silhouette of the
        * animal is top-heavy and that is most of the recognition. */}
      <ellipse cx="10" cy="5.6" rx="7.3" ry="4.9" />
      <path d="M7.7 10.2h4.6l-.5 3.5a2.1 2.1 0 0 1-3.6 0Z" />
      {/* THE EGG STRINGS, and they are the point. This column counts VOKSNE
        * HUNNLUS, and a pair of trailing egg strings is what distinguishes an
        * adult female from everything else on the fish. Without them the mark
        * was a rounded blob that read as a keyhole; with them it is the exact
        * animal the number is about. */}
      <path d="M8.9 13.1c-.5 3.4-.9 6.7-1.1 9.9a.8.8 0 0 1-1.6-.1c.2-3.4
               .6-6.8 1.2-10.1Z" />
      <path d="M11.1 13.1c.5 3.4.9 6.7 1.1 9.9a.8.8 0 0 0 1.6-.1c-.2-3.4
               -.6-6.8-1.2-10.1Z" />
    </svg>
  )
}

/** The direction as its own sign. Four shapes, no more. */
const PILER: Record<Pil, string> = {
  ned: 'M8 2.5v11M3.6 9.2 8 13.6l4.4-4.4',
  flat: 'M2.5 8h11M9.6 4.2 13.4 8l-3.8 3.8',
  opp: 'M8 13.5v-11M3.6 6.8 8 2.4l4.4 4.4',
  opp_bratt: 'M8 14.2V2M3.6 6.4 8 2l4.4 4.4M3.6 11 8 6.6l4.4 4.4',
}
const PILTEKST: Record<Pil, string> = {
  ned: 'på vei ned', flat: 'flatt', opp: 'på vei opp', opp_bratt: 'opp, bratt',
}

function Arrow({ pil }: { pil: Pil }) {
  return (
    <svg viewBox="0 0 16 16" width="19" height="19" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
      strokeLinejoin="round" className="glyf-pil" aria-hidden="true">
      <path d={PILER[pil] ?? PILER.flat} />
    </svg>
  )
}

/** The lice and the arrow together. `role="img"` with the whole claim in plain
 *  words, so a screen reader does not have to count glyphs. */
function Ikon({ antall, pil, label }: { antall: number; pil: Pil; label: string }) {
  return (
    <span className={`ikon b-${baand(antall)}`} role="img" aria-label={label}>
      <span className="ikon-lus">
        {Array.from({ length: Math.max(1, antall) }, (_, i) => <Louse key={i} />)}
      </span>
      <Arrow pil={pil} />
    </span>
  )
}

/* ------------------------------------------------------------------ panel */

/* --------------------------------------------------------------- the weeks */

/** Monday of the ISO week `iso` falls in, as YYYY-MM-DD.
 *
 *  Normalised through UTC so a row's Monday and this Monday are the same kind
 *  of thing before they are subtracted. `getUTCDay()` calls Sunday 0 and ISO
 *  calls it 7 — the off-by-one that puts every Sunday in the wrong week if it
 *  is not handled right here. */
export function mandag(iso: string): string {
  const d = new Date(iso)
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() || 7) - 1))
  return t.toISOString().slice(0, 10)
}

const UKE_MS = 604800000

/** The Monday of an ISO week, which is the key the two branches are joined on.
 *
 *  4 January is in ISO week 1 by definition, in every year, which is the one
 *  fixed point the whole calendar can be built from without a table. */
function mandagFraUke(aar: number, uke: number): string {
  const jan4 = Date.UTC(aar, 0, 4)
  const uke1 = jan4 - ((new Date(jan4).getUTCDay() || 7) - 1) * 86400000
  return new Date(uke1 + (uke - 1) * UKE_MS).toISOString().slice(0, 10)
}

/** Whole weeks from this Monday to that one. Negative is the past. Both ends
 *  are UTC midnight, so no summer-time transition can round this to 6.99. */
function ukeAvstand(iso: string, naa: string): number {
  return Math.round((Date.parse(mandag(iso)) - Date.parse(mandag(naa))) / UKE_MS)
}

/** The Monday `n` weeks from now, for a row that has no data of its own to
 *  take a date from. */
function ukeMandag(n: number, naa: string): string {
  return new Date(Date.parse(mandag(naa)) + n * UKE_MS).toISOString().slice(0, 10)
}

/** What to call the row: words for the four weeks a person has a word for, and
 *  the Monday date for the rest — which is what a weather forecast does once it
 *  runs out of "i morgen". */
function etikett(n: number, mandagIso: string): string {
  if (n === -2) return 'To uker siden'
  if (n === -1) return 'Forrige uke'
  if (n === 0) return 'Denne uken'
  if (n === 1) return 'Neste uke'
  return dagMnd(mandagIso)
}

/** The level band as a glyph count, by the rule the API publishes as `regel` on
 *  /v2/symboler: under half the limit is low, half to whole is medium, at or
 *  over the limit is over. The model's own symbols carry exactly these counts —
 *  1, 2, 3 — so a measured week and a forecast week are drawn on one scale and
 *  can be compared down the column.
 *
 *  Used for observations only. It is the API's rule rather than ours, which is
 *  what makes reading a measured value against it a lookup and not an opinion.
 *  The ARROW has no such rule and is never derived here.
 *
 *  NULL WHEN THE LIMIT IS NOT KNOWN, and the row then shows its number with no
 *  pictogram. Every band is a fraction OF the limit, so without one there is no
 *  band -- and the tempting default is the dangerous one: one louse is the
 *  reassuring end of the scale, and it would be drawn for a value that might be
 *  double the limit. Better to draw no claim than the wrong quiet one. */
export function antallFor(verdi: number, grense: number | null): number | null {
  if (grense == null || grense <= 0) return null
  if (verdi >= grense) return 3
  return verdi >= grense / 2 ? 2 : 1
}

interface Rad {
  n: number
  etikett: string
  verdi: number | null
  p10?: number
  p90?: number
  /** Glyph count, which is also the level band. Null means the week has no
   *  figure at all, and the row says so rather than drawing one louse. */
  antall: number | null
  /** Forecast rows only. Its absence is what makes an observation look like an
   *  observation. */
  pil?: Pil
  grense: number | null
  varslet: boolean
  fare: boolean
  /** The same week if the boat comes. Absent unless a delousing is being asked
   *  about, and absent on weeks the delousing answer does not reach. */
  etter?: { verdi: number; p10: number; p90: number; knock: boolean }
  /** `2026-W35` when a boat may be asked about for this week, null otherwise.
   *  Null on every week already lived through — you cannot book a boat into
   *  the past — and null past the eight weeks the model was measured on. */
  ukeKey: string | null
  valgt: boolean
  /** A delousing that already happened that week. Only ones somebody told us
   *  about — see `avlusetUker`. */
  avluset: boolean
}

function fraVarsel(w: VarselUke, n: number): Rad {
  const inst = w.data.instant.details
  const u = w.data.uken!
  return {
    n, etikett: etikett(n, mandag(w.time)), verdi: inst.voksneHunnlus,
    p10: inst.p10, p90: inst.p90,
    /* THE LEVEL IS CLASSIFIED ON THE NUMBER THIS ROW SHOWS, not on
       `summary.ikonAntall`.
       
       Their `lice2.outlook.classify` bands `median / limit`, and since v2b the
       figure we publish is the MEAN. On a right-skewed fan the mean sits above
       the median -- 0.617 against 0.480 at Ljøsøy in W35, a 29 % gap -- so the
       model's own glyph said "under the limit" on three weeks whose printed
       number was over it. Two statistics in one column, and the reader has no
       way to know which one the picture is about.
       
       The bands are identical to theirs (half the limit, then the limit), so
       this is not a second vocabulary; it is the same vocabulary applied to
       the quantity actually on screen. Their doctrine says so first: "the
       symbol says lavt og blir lavt; the number beside it says 0.04" -- the
       picture summarises the measurement shown, or it is summarising nothing.
       
       This is not cosmetic. `fare` -- the delousing warning -- fires on three
       lice, so a farm under the limit today whose forecast crosses got no
       warning at all while the median stayed below. */
    antall: antallFor(inst.voksneHunnlus, u.details.grense),
    /* The arrow stays theirs. Trend is a separate sign about direction, taken
       as a ratio against the origin, and a direction cannot contradict a level
       the way a miscounted glyph can. */
    pil: u.summary.pil, grense: u.details.grense, varslet: true, fare: false,
    ukeKey: null, valgt: false, avluset: false,
  }
}

/** Three weeks of what happened, then the weeks the model will commit to.
 *
 *  Every row is placed by its own Monday against this Monday. Nothing is
 *  placed by horizon index: on a register-anchored site the register runs about
 *  two weeks behind, so `h=1` is a week that has already been lived through,
 *  and a table that trusted `h` would label it "neste uke". */
/** Which past weeks a boat actually came to.
 *
 *  Two sources, and one deliberately left out. The farm's own record and the
 *  register's weekly flag are both things somebody TOLD us. `suspected` — a
 *  delousing inferred from a wellboat's movements — is not drawn: this is the
 *  farm's own page, they know whether they deloused, and a glyph that cannot
 *  say "we think" would assert an inference as a fact. The schema keeps the
 *  three sources apart precisely so they are not confused, and drawing them
 *  identically here would undo that. */
function avlusetUker(treatments: LiceTreatment[], register: RegisterWeek[],
                     naa: string): Set<number> {
  const ut = new Set<number>()
  const merk = (dato: string) => {
    const n = ukeAvstand(dato, naa)
    if (n <= 0 && n >= -2) ut.add(n)
  }
  for (const t of treatments) if (t.source !== 'suspected') merk(t.treated_at)
  for (const r of register) if (r.treated) merk(r.week)
  return ut
}

function byggRader(varsel: VarselData, counts: LiceCount[],
                   register: RegisterWeek[], cur: LiceCurrent | undefined,
                   naa: string, treatments: LiceTreatment[] = []): Rad[] {
  const uker = (varsel.timeseries ?? []).filter((w) => w.data.uken)

  /* The observations, one per week, from four sources in increasing order of
     authority. Later writes win, which is also what settles two counts in one
     week in favour of the later one.

     The register's weekly series first, because it is the broadest and the
     oldest: BarentsWatch and FDIR publish about two weeks behind the week they
     describe, so it reaches back where nothing else does and can never speak
     for this week. Then the register's current figure, then the model's own
     origin row, and last the farm's own counts — theirs last because theirs
     were taken at the cage, and a published average must not beat a count. */
  const obs = new Map<number, { verdi: number; dato: string }>()
  const sett = (dato?: string | null, verdi?: number | null) => {
    if (!dato || verdi == null) return
    const n = ukeAvstand(dato, naa)
    if (n < -2 || n > 0) return
    obs.set(n, { verdi, dato })
  }
  for (const r of register) sett(r.week, r.adult_female)
  if (cur) sett(cur.as_of, cur.adult_female)
  const origo = (varsel.timeseries ?? []).find((w) => !w.data.uken)
  if (origo) sett(origo.time, origo.data.instant.details.voksneHunnlus)
  for (const c of [...counts].sort((a, b) => a.counted_at.localeCompare(b.counted_at))) {
    sett(c.counted_at, c.adult_female)
  }

  /* The limit in force that week, from the forecast row that covers it. Past
     weeks usually have no such row, and then the operative limit is the one the
     receiver already resolved for the farm — never a hardcoded 0,5. */
  const grenseFor = (n: number): number | null =>
    uker.find((x) => ukeAvstand(x.time, naa) === n)?.data.uken?.details.grense
    ?? cur?.threshold ?? null

  const ut: Rad[] = []

  for (const n of [-2, -1, 0]) {
    const o = obs.get(n)
    if (o) {
      ut.push({
        n, etikett: etikett(n, mandag(o.dato)), verdi: o.verdi,
        antall: antallFor(o.verdi, grenseFor(n)), grense: grenseFor(n),
        varslet: false, fare: false, ukeKey: null, valgt: false,
        avluset: false,
      })
      continue
    }
    /* Nothing was measured that week. A forecast row may still cover it — that
       is the register-anchored case again — and the model's answer, wearing the
       arrow that marks it as one, beats a blank in the middle of three weeks
       the reader lived through. */
    const w = uker.find((x) => ukeAvstand(x.time, naa) === n)
    ut.push(w ? fraVarsel(w, n) : {
      n, etikett: etikett(n, ukeMandag(n, naa)), verdi: null,
      antall: null, grense: grenseFor(n), varslet: false, fare: false,
      ukeKey: null, valgt: false, avluset: false,
    })
  }

  for (const w of uker) {
    const n = ukeAvstand(w.time, naa)
    if (n >= 1) ut.push(fraVarsel(w, n))
  }

  /* Which weeks a boat may be asked about: this week onwards, out to the eight
     the model was measured on. The picker IS the table, so a row that cannot be
     picked must not look like one. */
  const avluset = avlusetUker(treatments, register, naa)
  for (const r of ut) {
    const m = ukeMandag(r.n, naa)
    r.avluset = avluset.has(r.n)
    /* A week the boat already came to is not a week you would book one for, so
       the fact takes the cell and the picker stands down. One column, one
       meaning: the boat came, or the boat could come. */
    r.ukeKey = !r.avluset && r.n >= 0 && r.n <= 8
      ? `${isoAar(m)}-W${isoUke(m)}` : null
    r.valgt = false
  }

  /* The first week the lice reach the limit, wherever that falls. On a farm
     already over it, that is a row above the forecast — and the delousing is
     this week's problem, not the first future week's that happens to agree. */
  const forst = ut.find((r) => r.antall === 3)
  if (forst) forst.fare = true

  return ut
}

/* ------------------------------------------------------------------ panel */

/** A warning triangle, drawn like the arrow beside it: one stroke weight and no
 *  fill, so it reads as a sign rather than a sticker. */
/** Hang the with-boat branch on the weeks that already exist, BY CALENDAR.
 *
 *  Never by position. The two answers do not share an origin offset — the
 *  delousing answer is quoted from the boat's week, the forecast from the
 *  count's — so a join by index slides one column against the other and looks
 *  entirely reasonable while doing it. Both sides carry `aar`/`uke` precisely
 *  so that this can be a lookup rather than an assumption.
 *
 *  A week the delousing answer covers but the forecast does not gets dropped
 *  rather than shown alone: a lone number in a comparison column is not a
 *  comparison, and the reader cannot tell which branch they are looking at. */
function flettAvlusing(rader: Rad[], av: AvlusingSvar | null | undefined,
                       naa: string): Rad[] {
  if (!av?.available) return rader
  const ved = new Map<string, { verdi: number; p10: number; p90: number; knock: boolean }>()
  if (av.avlusing) {
    const a = av.avlusing
    ved.set(mandagFraUke(a.aar, a.uke), {
      verdi: a.voksneHunnlus.snitt, p10: a.voksneHunnlus.q10,
      p90: a.voksneHunnlus.q90, knock: true,
    })
  }
  for (const e of av.etterpaa ?? []) {
    ved.set(mandagFraUke(e.aar, e.uke),
      { verdi: e.snitt, p10: e.q10, p90: e.q90, knock: false })
  }
  return rader.map((r) => {
    const hit = ved.get(ukeMandag(r.n, naa))
    return hit ? { ...r, etter: hit } : r
  })
}

/** A wellboat, because that is what comes. Not a droplet, not a syringe, not a
 *  chemical flask: the farmer's mental model of a delousing is a boat tying up
 *  alongside, and a symbol that matches the mental model needs no caption.
 *
 *  Drawn at 24 and shown at 20, hull and deckhouse only. A crane and a rail
 *  survive neither the size nor the greyscale it sits in when unselected. */
function Baat() {
  return (
    <svg viewBox="0 0 24 24" width="23" height="23" aria-hidden="true"
      focusable="false" fill="currentColor">
      <path className="baat-skrog" d="M2.5 14.6h19l-2.2 4.4a2.2 2.2 0 0
               1-2 1.2H6.7a2.2 2.2 0 0 1-2-1.2L2.5 14.6Z" />
      {/* The drop is what makes it a DELOUSING vessel rather than a boat.
        * Without it the glyph says "a ship comes that week", which is a
        * different sentence from the one the row is asking.
        *
        * Centred directly over the hull, and only two shapes in the whole
        * mark. At twenty pixels a deckhouse is a five-pixel blob that adds
        * noise and no meaning, and a drop set off to one side stops belonging
        * to the boat and starts floating beside it. Water above a hull is the
        * whole sentence; everything else was detail that does not survive the
        * size it is read at. */}
      <path className="baat-drape" d="M12 3.6c2.2 2.7 3.2 4.5 3.2 5.8a3.2
               3.2 0 0 1-6.4 0c0-1.3 1-3.1 3.2-5.8Z" />
    </svg>
  )
}

function Fare() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" className="glyf-fare" aria-hidden="true">
      <path d="M12 3.9 21.5 20.1H2.5Z" />
      <path d="M12 9.8v4.2" />
      <path d="M12 17.2h.01" />
    </svg>
  )
}

/** One week. Four cells on screen, one sentence to a screen reader: the row is
 *  a single claim, and nothing is served by making a listener reassemble it
 *  from a label, a glyph count and a parenthesis. */
function RadVis({ r, onVelg, laster, indeks, valgtUke }: {
  r: Rad
  onVelg?: (uke: string | null) => void
  laster?: boolean
  /** Row position, only so the second column can sweep in one row after
   *  another instead of all at once. */
  indeks: number
  valgtUke?: string | null
}) {
  // Red marks ONE number, not the whole row. If the upper end of the interval
  // crosses while the estimate sits under it, the upper end is the news.
  const over = (x: number) => (r.grense != null && x > r.grense ? 'over' : undefined)

  const paastand = [
    r.etikett,
    r.verdi == null ? 'ikke talt'
      : `${r.varslet ? 'varsel' : 'talt'} ${nb(r.verdi)}`,
    r.varslet && r.p10 != null && r.p90 != null
      ? `åtte av ti ganger mellom ${nb(r.p10)} og ${nb(r.p90)}` : '',
    r.varslet && r.pil ? (PILTEKST[r.pil] ?? '') : '',
    r.grense != null ? `grensen er ${nb(r.grense)}` : '',
    r.fare ? 'over grensen, avlus denne uken' : '',
    r.avluset ? 'avluset denne uken' : '',
    r.etter ? `med avlusing ${nb(r.etter.verdi)}, `
      + `åtte av ti ganger mellom ${nb(r.etter.p10)} og ${nb(r.etter.p90)}` : '',
  ].filter(Boolean).join(', ') + '.'

  return (
    <li className={`v-rad ${r.varslet ? 'frem' : 'obs'}${r.fare ? ' fare' : ''}`
      + `${r.valgt ? ' valgt' : ''}${r.n === 0 ? ' naa' : ''}`}
      aria-label={paastand}>
      <span className="v-nar" aria-hidden="true">{r.etikett}</span>

      {/* The boat, on every week one could come to. Faint until it is here,
        * solid when it is. No caption: a wellboat alongside IS the farmer's
        * picture of a delousing, and a symbol that matches the picture does
        * not need to be told what it means. Always drawn rather than revealed
        * on hover, because a phone has no hover and a control nobody can find
        * is not a control. */}
      <span className="v-baat">
        {/* It already happened. Not a button, because there is nothing to
          * press: the same mark, stated rather than offered. Without it a week
          * the boat came to looks like natural decline, which is the one
          * reading of this table that is flatly wrong. */}
        {r.avluset && <span className="baat-var" title="Avluset"><Baat /></span>}
        {r.ukeKey && onVelg && (
          <button type="button" aria-pressed={r.valgt} disabled={laster}
            className={`baat-knapp${r.valgt ? ' valgt' : ''}`
              + `${laster && r.valgt ? ' laster' : ''}`}
            aria-label={`${r.valgt ? 'Fjern' : 'Vis'} avlusing `
              + `${r.etikett.toLowerCase()}`}
            onClick={() => onVelg(r.valgt ? null : r.ukeKey)}>
            <Baat />
          </button>
        )}
      </span>

      <span className="v-ikon" aria-hidden="true">
        {r.antall == null ? <span className="v-tom">—</span> : (
          <span className={`ikon b-${baand(r.antall)}`}>
            <span className="ikon-lus">
              {Array.from({ length: r.antall }, (_, i) => <Louse key={i} />)}
            </span>
            {r.pil && <Arrow pil={r.pil} />}
          </span>
        )}
      </span>

      <span className="v-tall" aria-hidden="true">
        {r.verdi == null ? <span className="v-tom">—</span> : (
          <>
            <span className={over(r.verdi)}>{nb(r.verdi)}</span>
            {r.p10 != null && r.p90 != null && (
              <span className="v-omr">
                {' ('}<span className={over(r.p10)}>{nb(r.p10)}</span>
                {'–'}<span className={over(r.p90)}>{nb(r.p90)}</span>{')'}
              </span>
            )}
          </>
        )}
      </span>

      {/* The other branch, when one has been asked for. No pictogram: two
        * icon sets on one row is noise, and the number is the comparison. */}
      {r.etter && (
        /* Keyed on the week asked about, so picking a different one replays
           the sweep rather than swapping numbers in place. The answer changed;
           the table should look like it changed. */
        <span key={valgtUke ?? ''} aria-hidden="true"
          style={{ ['--i' as string]: indeks }}
          className={`v-etter${r.etter.knock ? ' knock' : ''}`}>
          <span className={over(r.etter.verdi)}>{nb(r.etter.verdi)}</span>
          <span className="v-omr">
            {' ('}<span className={over(r.etter.p10)}>{nb(r.etter.p10)}</span>
            {'–'}<span className={over(r.etter.p90)}>{nb(r.etter.p90)}</span>{')'}
          </span>
        </span>
      )}

      <span className="v-fare" aria-hidden="true">
        {r.fare && <><Fare /><span>Avlus</span></>}
      </span>
    </li>
  )
}

export function Varsel({ varsel, symbols, counts, register, current, rerunning,
                        avlusing, valgtUke, onVelgUke, avlusingLaster,
                        treatments }: {
  varsel: VarselData; symbols?: SymbolTable | null
  /** The farm's own counts, which fill the three weeks of observations above
   *  the forecast. Without them those rows fall back to the register, which is
   *  weeks behind — the table still reads, with fewer numbers in it. */
  counts?: LiceCount[]
  /** Delousings already on record, so a week the boat came to says so. */
  treatments?: LiceTreatment[]
  /** The register's weekly series, which is what fills the weeks nobody at the
   *  farm counted. Without it the oldest rows are honest em dashes. */
  register?: RegisterWeek[]
  current?: LiceCurrent
  /** A count has just been saved and the model is rebuilding this. The table
   *  stays on screen and dims rather than collapsing to skeletons: it is about
   *  to be compared against, and replacing a real forecast with grey bars
   *  throws away the thing the reader is here to see. */
  rerunning?: boolean
  /** What a delousing in some chosen week would buy, if one has been asked
   *  about. Its weeks are hung on the rows by calendar; the panel is the same
   *  table with one more column, not a second table. */
  avlusing?: AvlusingSvar | null
  /** `2026-W35`, the week the boat is being asked about, or null. Lifted out
   *  of this component because the answer is fetched where the site id lives. */
  valgtUke?: string | null
  onVelgUke?: (uke: string | null) => void
  avlusingLaster?: boolean
}) {
  const t = useT()

  // A fallow site gets a forecast dated to whenever it last reported, and the
  // API is honest about that rather than refusing. A page that did not check
  // would show a years-old answer as today's.
  if (varsel.meta?.syklus && varsel.meta.syklus !== 'live') {
    return (
      <p className="muted" style={{ margin: 0 }}>
        {t('This farm is fallow, so there is nothing to forecast.')}
      </p>
    )
  }

  const naa = new Date().toISOString().slice(0, 10)
  const rader = flettAvlusing(
    byggRader(varsel, counts ?? [], register ?? [], current, naa,
              treatments ?? []), avlusing, naa)
    .map((r) => (r.ukeKey && r.ukeKey === valgtUke ? { ...r, valgt: true } : r))
  const medBaat = rader.some((r) => r.etter)
  // Three observation rows and nothing ahead of them is a history, not a
  // forecast, and this panel does not claim to be one.
  if (!rader.some((r) => r.varslet)) return null

  return (
    <div className={`varsel${rerunning ? ' rerunning' : ''}`}
      aria-busy={rerunning || undefined}>
      {rerunning && (
        <p className="varsel-kjorer">
          <Eq label={t('Running the model again')} />
          {t('Running the model again')} …
        </p>
      )}

      {/* No header until there are two columns. One column of numbers under a
        * heading that says "uten avlusing" invites the question of where the
        * other one is. */}
      <ul className={`varsel-liste${medBaat ? ' med-baat' : ''}`
        + `${avlusingLaster ? ' laster' : ''}`}>
        {medBaat && (
          <li className="v-hode" aria-hidden="true">
            <span className="v-nar" />
            <span className="v-baat" />
            <span className="v-ikon" />
            <span className="v-tall">{t('Without delousing')}</span>
            <span className="v-etter">{t('With delousing')}</span>
            <span className="v-fare" />
          </li>
        )}
        {rader.map((r, i) => (
          <RadVis key={r.n} r={r} onVelg={onVelgUke} laster={avlusingLaster}
            indeks={i} valgtUke={valgtUke} />
        ))}
      </ul>

      {/* THE ANSWER IS CONDITIONAL ON THE DELOUSING WORKING, and the model says
        * so in its own words rather than ours. Their knockdown is fitted on
        * campaigns whose next count came in below the one before, so a pass
        * that moved nothing is not in the frame it learnt from. Without this
        * sentence the fan reads as a promise about booking a boat, which is a
        * different and much stronger claim than the one being made. */}
      {medBaat && (
        <p className="varsel-fot faint">
          {t('Assumes the delousing works.')}
        </p>
      )}

      {/* The one thing a farmer who just typed a count wants to know: did it
        * count for anything. `kilde` is the model's own answer, so the page
        * states it rather than inferring it from a zero — and stays silent
        * rather than claiming it when the count did not reach the origin.
        *
        * It is worth stating because it is worth something: their side measured
        * a two-week-stale origin at +25.9 % CRPS at two weeks and +13.3 % at
        * four, and the register runs a week behind the week it describes. */}
      {varsel.meta?.origo?.kilde === 'telling' && (
        <p className="varsel-kilde">
          {t('Built on your own count from')}{' '}
          {new Date(varsel.meta.origo.dato).toLocaleDateString('nb-NO',
            { day: 'numeric', month: 'long' })}.
        </p>
      )}

      {/* A reading we stored but the model would not take. Its band is
        * 0.5–25 °C against the −2–30 the wire accepts, so this is reachable
        * with a real thermometer on a cold winter morning. Said out loud,
        * because a farm that typed a temperature into a lice portal will
        * otherwise assume the forecast used it. */}
      {(varsel.meta?.tellinger?.temperaturForkastet ?? 0) > 0 && (
        <p className="varsel-fot faint">
          {t('The model would not take the temperature you entered:')}{' '}
          {varsel.meta!.tellinger!.temperaturForkastet}.
        </p>
      )}

      {/* Weeks the model had to invent between the register's last row and the
        * count. They carry no treatment record, so a delousing in that gap is
        * invisible to it. Normally 0. */}
      {(varsel.meta?.tellinger?.syntetiskeUker ?? 0) > 0 && (
        <p className="varsel-fot faint">
          {t('The weeks between the register and your count carry no treatment record:')}{' '}
          {varsel.meta!.tellinger!.syntetiskeUker}.
        </p>
      )}

      <Legende varsel={varsel} symbols={symbols} />
    </div>
  )
}

/* ---------------------------------------------------------------- legende */

/** The two claims that justify the symbol's form — computed from the table
 *  sitting right above them, not written in by hand.
 *
 *  They stood first as percentages in the prose, copied from the report. Then
 *  they would have been true until the next retraining and wrong afterwards,
 *  side by side with a table showing the new figures. A caption that
 *  contradicts its own picture is worse than no caption.
 *
 *  If a symbol falls under a hundred cases the sentence does not appear. Then
 *  the claim no longer exists, and it should not be retold from memory. */
function Historier({ symbols, uker }: { symbols: SymbolTable; uker: number }) {
  const ved = (k: Symbolkode) =>
    symbols.symboler?.find((x) => x.symbol === k)?.verifisert?.[String(uker)]

  const out: React.ReactNode[] = []

  const stigende = (['lavt_stabilt', 'lavt_stiger', 'lavt_vokser_raskt'] as const)
    .map((k) => ved(k)?.andelSteg)
  if (stigende.every((x): x is number => x != null)) {
    out.push(
      <p className="bildetekst" key="pil">
        <b>Pilen tjener til føden. </b>
        Blant de tre lave varslene steg lusa faktisk{' '}
        {stigende.map((x) => Math.round(x * 100)).join(', ')} prosent av gangene.
        Samme lus, samme nivå — bare pilen skiller dem, og den skiller riktig.
      </p>
    )
  }

  const nivaa = (['lavt_stabilt', 'middels_stabilt', 'over_grensen'] as const)
    .map((k) => ved(k)?.andelUnderGrense)
  if (nivaa.every((x): x is number => x != null)) {
    out.push(
      <p className="bildetekst" key="nivaa">
        <b>Antallet lus tjener til føden. </b>
        Ett symbol gikk over grensen i {prosent(1 - nivaa[0]!)} av tilfellene, to
        symboler i {prosent(1 - nivaa[1]!)}, tre symboler i{' '}
        {prosent(1 - nivaa[2]!)}. Nivåbåndet er ikke pynt.
      </p>
    )
  }
  return <>{out}</>
}

/** The whole closed set, with the frequency for the horizon this page actually
 *  forecasts. Two things fall out of the table by themselves, and they are the
 *  reason the symbol has the shape it has. */
function Legende({ varsel, symbols }: {
  varsel: VarselData; symbols?: SymbolTable | null
}) {
  const t = useT()
  const uker = (varsel.timeseries ?? []).filter((w) => w.data.uken).length

  return (
    <details className="varsel-legende">
      <summary>{t('How to read the forecast')}</summary>
      <div className="innmat">
        {/* The caveat, verbatim from the API. It used to sit in its own box
            under the strip and repeat the lead; here it stands once, where a
            reader goes to find out what the forecast actually claims. */}
        {varsel.forbehold && (
          <p className="bildetekst" style={{ margin: '0 0 16px' }}>
            <b>Forbehold. </b>{varsel.forbehold}
          </p>
        )}

        {!symbols ? (
          <p className="bildetekst" style={{ margin: 0 }}>
            Symbolforklaringa kunne ikke hentes.
          </p>
        ) : (
          <>
            {symbols.regel && (
              <p className="bildetekst" style={{ margin: '0 0 14px' }}>
                {symbols.regel}
              </p>
            )}

            <table className="legende-tbl">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Varsel</th>
                  <th className="n">Gikk over grensen</th>
                  <th className="n">Steg</th>
                  <th className="n">Tilfeller</th>
                </tr>
              </thead>
              <tbody>
                {(symbols.symboler ?? []).map((s) => {
                  const v = s.verifisert?.[String(uker)]
                  const tynn = !v || v.andelUnderGrense == null
                  return (
                    <tr key={s.symbol}>
                      <td>
                        <Ikon antall={s.ikon_antall} pil={s.pil} label={s.tekst} />
                      </td>
                      <td>{s.tekst}</td>
                      <td className={`n ${tynn ? 'tynn' : ''}`}>
                        {tynn ? 'for få' : prosent(1 - v!.andelUnderGrense!)}
                      </td>
                      <td className={`n ${tynn ? 'tynn' : ''}`}>
                        {tynn ? 'for få' : prosent(v!.andelSteg!)}
                      </td>
                      <td className="n tynn">
                        {(v?.n ?? 0).toLocaleString('nb-NO')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <Historier symbols={symbols} uker={uker} />

            <p className="bildetekst">
              <b>Tallet i parentes er spennet. </b>
              «0,32 (0,16–0,52)» vil si at anslaget er 0,32, og at tellingen
              havner mellom 0,16 og 0,52 i åtte av ti tilfeller. Står et av
              tallene i rødt, ligger nettopp det tallet over grensen — også når
              anslaget selv ikke gjør det.
            </p>

            <p className="bildetekst">
              <b>Tallene kan hoppe opp og ned. </b>
              Modellen tilpasser ett eget anslag per uke — fire uavhengige anslag
              fra samme telling, ikke én simulert bane. Over de aktive anleggene
              er det typiske spranget mellom to uker 0,04, mens båndet er 0,18.
              Sikksakken er altså en firedel av usikkerheten, og ikke en varslet
              svingning. Symbolet står stille gjennom slike sprang.
            </p>

            <p className="bildetekst">
              «For få» betyr under hundre tilfeller ut av fold. Da finnes det
              ikke et tall, og da står det ikke et.
            </p>

            {varsel.meta?.horisont?.grunn && (
              <p className="bildetekst">{varsel.meta.horisont.grunn}.</p>
            )}

            {symbols.kilde && (
              <p className="bildetekst">Talt på {symbols.kilde}.</p>
            )}
          </>
        )}
      </div>
    </details>
  )
}
